import React, { useState, useEffect } from "react";
import {
  FaBars,
  FaTimes,
  FaGithub,
  FaLinkedin,
  FaCode,
  FaLaptopCode,
  FaTools,
} from "react-icons/fa";
import { BsFillPersonLinesFill, BsWhatsapp } from "react-icons/bs";
import { Link } from "react-scroll";
import { getResumeUrl } from "./Resume";

const Navbar = () => {
  const [nav, setNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleClick = () => setNav(!nav);
  const resumeUrl = getResumeUrl();

  useEffect(() => {
    document.documentElement.classList.add("dark");
    document.documentElement.classList.remove("light");
    localStorage.setItem("portfolio_theme", "dark");
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", target: "home" },
    { name: "Projects", target: "work" },
    { name: "Experience", target: "experience" },
    { name: "Skills", target: "skills" },
    { name: "Contact", target: "contact" },
  ];

  return (
    <>
      <nav
        className={`fixed top-4 left-1/2 -translate-x-1/2 w-[92%] max-w-6xl z-50 transition-all duration-300 rounded-2xl ${
          scrolled
            ? "bg-[#0F172A]/95 backdrop-blur-2xl border border-slate-700/90 shadow-2xl py-3 px-6 text-slate-100"
            : "bg-[#0F172A]/85 backdrop-blur-xl border border-slate-700/70 shadow-xl py-3.5 px-6 text-slate-100"
        }`}
      >
        <div className="w-full flex justify-between items-center">
          {/* Brand Logo */}
          <Link
            to="home"
            smooth={true}
            duration={500}
            className="cursor-pointer flex items-center gap-2.5 group"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 via-sky-600 to-[#00B4DB] flex items-center justify-center text-white font-extrabold text-lg shadow-md shadow-blue-500/25 group-hover:scale-105 transition-transform">
              <FaCode />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base sm:text-lg font-extrabold tracking-tight transition-colors text-white group-hover:text-[#00B4DB]">
                Partha<span className="text-[#00B4DB]">.dev</span>
              </span>
              <span className="w-2 h-2 rounded-full bg-[#00B4DB] animate-pulse hidden sm:inline-block" title="Online & Available" />
            </div>
          </Link>

          {/* Desktop Nav Items */}
          <div className="hidden md:flex items-center gap-1 p-1.5 rounded-xl border border-slate-700/80 bg-[#1E293B]/80 backdrop-blur-md transition-colors">
            {navLinks.map((link, idx) => (
              <Link
                key={idx}
                to={link.target}
                smooth={true}
                spy={true}
                activeClass="bg-[#00B4DB]/15 text-[#00B4DB] border-[#00B4DB]/40 font-bold shadow-sm"
                duration={500}
                className="px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-lg transition-all cursor-pointer border border-transparent text-slate-300 hover:text-white hover:bg-slate-800"
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={handleClick}
              className="text-slate-200 hover:text-[#00B4DB] focus:outline-none p-2 rounded-lg bg-[#1E293B] border border-slate-700"
              aria-label="Toggle menu"
            >
              {!nav ? <FaBars size={20} /> : <FaTimes size={20} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {nav && (
        <div className="fixed inset-0 z-40 bg-[#0F172A]/98 backdrop-blur-3xl flex flex-col justify-center items-center md:hidden transition-all duration-300">
          <ul className="flex flex-col gap-6 text-center">
            {navLinks.map((link, idx) => (
              <li key={idx}>
                <Link
                  onClick={handleClick}
                  to={link.target}
                  smooth={true}
                  duration={500}
                  className="text-2xl font-semibold text-slate-200 hover:text-[#00B4DB] transition-colors"
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex gap-4 mt-12">
            <a
              href="https://github.com/IM-Partha"
              target="_blank"
              rel="noreferrer"
              className="p-3 bg-[#1E293B] border border-slate-700 rounded-full text-slate-300 hover:text-[#00B4DB] shadow-sm"
            >
              <FaGithub size={22} />
            </a>
            <a
              href="https://www.linkedin.com/in/partha-debnath-9630a7190/"
              target="_blank"
              rel="noreferrer"
              className="p-3 bg-[#1E293B] border border-slate-700 rounded-full text-slate-300 hover:text-[#00B4DB] shadow-sm"
            >
              <FaLinkedin size={22} />
            </a>
            <a
              href="https://wa.me/9476316652"
              target="_blank"
              rel="noreferrer"
              className="p-3 bg-[#1E293B] border border-slate-700 rounded-full text-slate-300 hover:text-[#00B4DB] shadow-sm"
            >
              <BsWhatsapp size={22} />
            </a>
          </div>
        </div>
      )}

      {/* Floating Side Quick Bar (Desktop) */}
      <div className="hidden lg:flex fixed flex-col top-[35%] left-0 z-40">
        <ul className="space-y-2">
          {/* Projects */}
          <li className="w-[160px] h-[50px] flex justify-between items-center ml-[-105px] hover:ml-[-5px] duration-300 bg-[#1E293B]/95 backdrop-blur-xl border border-slate-700 rounded-r-2xl shadow-xl hover:border-[#00B4DB]">
            <Link
              to="work"
              smooth={true}
              duration={500}
              className="flex justify-between items-center w-full px-4 text-slate-200 hover:text-[#00B4DB] font-semibold text-sm cursor-pointer"
            >
              Projects <FaLaptopCode size={22} className="text-[#00B4DB]" />
            </Link>
          </li>

          {/* Skills */}
          <li className="w-[160px] h-[50px] flex justify-between items-center ml-[-105px] hover:ml-[-5px] duration-300 bg-[#1E293B]/95 backdrop-blur-xl border border-slate-700 rounded-r-2xl shadow-xl hover:border-[#00B4DB]">
            <Link
              to="skills"
              smooth={true}
              duration={500}
              className="flex justify-between items-center w-full px-4 text-slate-200 hover:text-[#00B4DB] font-semibold text-sm cursor-pointer"
            >
              Skills <FaTools size={20} className="text-[#00B4DB]" />
            </Link>
          </li>

          {/* Resume */}
          <li className="w-[160px] h-[50px] flex justify-between items-center ml-[-105px] hover:ml-[-5px] duration-300 bg-[#1E293B]/95 backdrop-blur-xl border border-slate-700 rounded-r-2xl shadow-xl hover:border-[#00B4DB]">
            <a
              className="flex justify-between items-center w-full px-4 text-slate-200 hover:text-[#00B4DB] font-semibold text-sm"
              href={resumeUrl}
              target="_blank"
              rel="noreferrer"
            >
              Resume <BsFillPersonLinesFill size={22} className="text-[#00B4DB]" />
            </a>
          </li>
        </ul>
      </div>
    </>
  );
};

export default Navbar;
